import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { 
  Star, MessageSquare, Wallet, TrendingUp, TrendingDown, Minus,
  ShoppingBag, Clock, CheckCircle, XCircle,
  Camera, Plus, ArrowRight, Share2, Eye, Heart, Sparkles,
  ChevronRight, Target, DollarSign, Flame
} from "lucide-react";
import { Link } from "wouter";
import PartnerLayout from "./PartnerLayout";

interface DashboardStats {
  rating: number;
  ratingChange: number;
  totalReviews: number;
  newReviewsThisWeek: number;
  pendingReplies: number;
  kudozBalance: number;
  kudozEarnedThisMonth: number;
  activePromotions: number;
  loyalCustomers: number;
  influencerOffers: number;
}

interface RecentReview {
  id: string;
  rating: number;
  text: string;
  userName: string;
  createdAt: string;
  hasReply: boolean;
}

interface PartnerPost {
  id: string;
  text: string;
  postType: string;
  likes: number;
  comments: number;
  views?: number;
  orders?: number;
  revenue?: number;
  createdAt: string;
  images?: string[];
}

interface SocialConnection {
  id: string;
  platform: 'facebook' | 'instagram' | 'tiktok';
  platformAccountName: string | null;
  platformAccountHandle: string | null;
  isActive: boolean;
}

interface ManagerOrder {
  id: string;
  type: string;
  mode: string;
  customer: {
    name: string;
    phone?: string;
  };
  progress: {
    placed: number;
    confirmed?: number;
    ready?: number;
    completed?: number;
  };
  schedule: {
    pickup: number;
  };
  total: number;
}

interface OrdersResponse {
  total_results: number;
  total_pages: number;
  page: number;
  total_earned: number;
  daily_summary: Array<{
    sort: number;
    orders: number;
    earned: number;
  }>;
  results: ManagerOrder[];
}

interface SocialAnalytics {
  totalPosts: number;
  publishedPosts: number;
  failedPosts: number;
  pendingPosts: number;
  totalEngagement: {
    likes: number;
    comments: number;
    shares: number;
    views: number;
    reach: number;
  };
  platformBreakdown: Record<string, {
    posts: number;
    published: number;
    failed: number;
    engagement: { likes: number; comments: number; shares: number; views: number; reach: number; };
  }>;
  recentPosts: Array<{
    id: string;
    platform: string;
    status: string;
    platformPostUrl?: string | null;
    publishedAt?: string | null;
    createdAt: string | null;
    engagement?: Record<string, number> | null;
    errorMessage?: string | null;
  }>;
  directOrders?: number;
  revenueFromPosts?: number;
}

interface ActionCard {
  id: string;
  message: string;
  cta: string;
  href: string;
  icon: typeof MessageSquare;
  urgent?: boolean;
}

export default function PartnerDashboard() {
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const restaurantName = localStorage.getItem('partnerRestaurantName') || '';
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');
  const onboardingKey = `partnerOnboardingDismissed:${restaurantId || 'default'}`;
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => 
    localStorage.getItem(onboardingKey) === 'true'
  );
  
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ['/api/partner/dashboard/stats', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: recentReviews, isLoading: reviewsLoading } = useQuery<RecentReview[]>({
    queryKey: ['/api/partner/reviews/recent', restaurantId],
    enabled: !!restaurantId,
  });

  const { data: posts } = useQuery<PartnerPost[]>({
    queryKey: ['/api/partner/posts', restaurantId],
    enabled: !!restaurantId,
  });

  const authToken = localStorage.getItem('auth_token');

  const { data: socialConnections } = useQuery<SocialConnection[]>({
    queryKey: ['/api/partner/social/connections', restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/partner/social/connections?restaurantId=${restaurantId}`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'X-Eatzy-Token': eatzyToken || '',
        },
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: socialAnalytics } = useQuery<SocialAnalytics>({
    queryKey: ['/api/partner/social/analytics', restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/partner/social/analytics?restaurantId=${restaurantId}`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'X-Eatzy-Token': eatzyToken || '',
        },
      });
      if (!res.ok) return { totalPosts: 0, publishedPosts: 0, failedPosts: 0, pendingPosts: 0, totalEngagement: { likes: 0, comments: 0, shares: 0, views: 0, reach: 0 }, platformBreakdown: {}, recentPosts: [] };
      return res.json();
    },
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: ordersData, isLoading: ordersLoading, isError: ordersError } = useQuery<OrdersResponse>({
    queryKey: ['/api/partner/restmanager/orders', restaurantId],
    queryFn: async () => {
      const response = await fetch(`/api/partner/restmanager/${restaurantId}/orders`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'X-Eatzy-Token': eatzyToken || '',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication failed. Please log in again.');
        }
        throw new Error('Failed to fetch orders');
      }
      return response.json();
    },
    enabled: !!restaurantId && !!eatzyToken,
    retry: 1,
  });
  
  const getOrderStatus = (order: ManagerOrder) => {
    if (order.progress.completed) return { label: 'Completed', color: 'text-green-600', icon: CheckCircle };
    if (order.progress.ready) return { label: 'Ready', color: 'text-blue-600', icon: Clock };
    if (order.progress.confirmed) return { label: 'Confirmed', color: 'text-yellow-600', icon: Clock };
    return { label: 'Pending', color: 'text-orange-600', icon: Clock };
  };
  
  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const hasNoPosts = posts !== undefined && posts.length === 0;
  const hasConnectedSocial = (socialConnections || []).some(c => c.isActive);
  const showOnboarding = hasNoPosts && !onboardingDismissed;

  const handleDismissOnboarding = () => {
    localStorage.setItem(onboardingKey, 'true');
    setOnboardingDismissed(true);
  };

  const buildActionCards = (): ActionCard[] => {
    const cards: ActionCard[] = [];

    const unrepliedCount = recentReviews?.filter(r => !r.hasReply).length ?? (stats?.pendingReplies ?? 0);
    if (unrepliedCount > 0) {
      cards.push({
        id: 'reviews',
        message: `${unrepliedCount} review${unrepliedCount !== 1 ? 's' : ''} waiting for your reply`,
        cta: 'Reply now',
        href: '/partner/reviews',
        icon: MessageSquare,
        urgent: true,
      });
    }

    if (posts !== undefined && !hasNoPosts) {
      const mostRecent = posts[0];
      if (mostRecent) {
        const daysSince = Math.floor((Date.now() - new Date(mostRecent.createdAt).getTime()) / (1000 * 60 * 60 * 24));
        if (daysSince >= 7) {
          cards.push({
            id: 'post-stale',
            message: `Last post was ${daysSince} days ago — post today to stay visible and drive orders`,
            cta: 'QuickPost',
            href: '/partner/post?quickMode=1',
            icon: Camera,
          });
        }
      }
    }

    if (!hasConnectedSocial && socialConnections !== undefined) {
      cards.push({
        id: 'connect-social',
        message: 'Connect Instagram or Facebook to turn posts into orders',
        cta: 'Connect',
        href: '/partner/settings',
        icon: Share2,
      });
    }

    return cards.slice(0, 3);
  };

  const actionCards = buildActionCards();
  const allGood = actionCards.length === 0 && !statsLoading && stats !== undefined && !showOnboarding;

  const recentPostsForDisplay = (posts || []).slice(0, 3);
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-dashboard-title">
            {restaurantName || 'Home'}
          </h1>
          <p className="text-muted-foreground text-sm">
            Turn your food posts into direct orders
          </p>
        </div>

        {showOnboarding && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-6 pb-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 rounded-full bg-primary/10 p-3">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <p className="font-semibold text-lg leading-tight">Get started in 3 steps</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Post a photo of your food, and we'll turn it into an order link your customers can tap.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <OnboardingStep
                      number={1}
                      title="Connect your Instagram or Facebook"
                      description="Link your social accounts so posts go everywhere at once"
                      done={hasConnectedSocial}
                      href="/partner/settings"
                    />
                    <OnboardingStep
                      number={2}
                      title="Snap a photo of today's special"
                      description="Use our camera or pick from your gallery"
                      done={false}
                      href="/partner/post"
                    />
                    <OnboardingStep
                      number={3}
                      title="Hit Post — we handle the rest"
                      description="Your post goes live on Eatzy + your socials with an order link"
                      done={false}
                    />
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <Link href="/partner/post?quickMode=1">
                      <Button data-testid="button-onboarding-post">
                        <Camera className="h-4 w-4 mr-2" />
                        Create your first post
                      </Button>
                    </Link>
                    <button
                      onClick={handleDismissOnboarding}
                      className="text-sm text-muted-foreground hover:underline underline-offset-4"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!showOnboarding && (
          <Link href="/partner/post?quickMode=1">
            <Card className="hover-elevate cursor-pointer border-primary/20">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-4">
                  <div className="shrink-0 rounded-full bg-primary p-3">
                    <Plus className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">QuickPost</p>
                    <p className="text-sm text-muted-foreground">
                      Snap a photo, tag a dish, post to Eatzy + socials in one tap
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {!showOnboarding && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Your Impact</CardTitle>
              </div>
              <CardDescription>How your posts are driving business</CardDescription>
            </CardHeader>
            <CardContent>
              {socialAnalytics && socialAnalytics.directOrders && socialAnalytics.directOrders > 0 ? (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <div className="text-lg font-bold" data-testid="text-direct-orders">
                        {socialAnalytics.directOrders}
                      </div>
                      <p className="text-xs text-muted-foreground">Direct orders</p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold" data-testid="text-post-revenue">
                        ${(socialAnalytics.revenueFromPosts || 0).toFixed(0)}
                      </div>
                      <p className="text-xs text-muted-foreground">From posts</p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600" data-testid="text-commission-saved">
                        ${((socialAnalytics.revenueFromPosts || 0) * 0.15).toFixed(0)}
                      </div>
                      <p className="text-xs text-muted-foreground">Commission saved</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    vs. third-party delivery app fees (est. 15% commission)
                  </p>
                </>
              ) : (
                <div className="space-y-4">
                  {socialAnalytics && (socialAnalytics.totalEngagement.reach > 0 || socialAnalytics.totalEngagement.likes > 0 || socialAnalytics.totalEngagement.views > 0) && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <div className="text-lg font-bold" data-testid="text-total-reach">
                          {socialAnalytics.totalEngagement.reach.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">Reach</p>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold" data-testid="text-total-views">
                          {socialAnalytics.totalEngagement.views.toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">Views</p>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold" data-testid="text-total-engagement">
                          {(socialAnalytics.totalEngagement.likes + socialAnalytics.totalEngagement.comments + socialAnalytics.totalEngagement.shares).toLocaleString()}
                        </div>
                        <p className="text-xs text-muted-foreground">Engagements</p>
                      </div>
                    </div>
                  )}
                  <div className="text-center py-2 space-y-1">
                    <DollarSign className="h-5 w-5 mx-auto text-muted-foreground" />
                    <p className="text-sm font-medium">Coming soon: order tracking</p>
                    <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                      Once customers start ordering from your posts, you'll see direct orders, revenue, and commission savings here.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!showOnboarding && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-orange-500" />
                <CardTitle className="text-base">30-Day Guarantee</CardTitle>
              </div>
              <CardDescription>Post consistently and track your progress</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const now = new Date();
                const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                const daysRemaining = Math.max(0, Math.ceil((endOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                const totalDays = endOfMonth.getDate();

                const postsThisMonth = (posts || []).filter(p => {
                  const d = new Date(p.createdAt);
                  return d >= startOfMonth && d <= now;
                }).length;
                const monthlyPostGoal = 3;
                const postPct = Math.min(100, (postsThisMonth / monthlyPostGoal) * 100);

                const revenueThisMonth = socialAnalytics?.revenueFromPosts ?? (ordersData?.total_earned ?? 0);
                const revenueGoal = 500;
                const revenuePct = Math.min(100, (revenueThisMonth / revenueGoal) * 100);

                return (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>{postsThisMonth} of {monthlyPostGoal} posts</span>
                        <span className="font-semibold">{Math.round(postPct)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${postPct}%` }} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>${revenueThisMonth.toFixed(0)} of ${revenueGoal} goal</span>
                        <span className="font-semibold">{Math.round(revenuePct)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${revenuePct}%` }} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                      </span>
                      <span>Day {totalDays - daysRemaining} of {totalDays}</span>
                    </div>

                    {postsThisMonth >= monthlyPostGoal && revenueThisMonth >= revenueGoal ? (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Goals reached! You're on track this month.
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Post regularly to hit your monthly targets and maximize order visibility
                      </p>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {!showOnboarding && (
          <div className="space-y-3">
            {statsLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : allGood ? (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Everything looks great — keep posting to stay visible!</p>
              </div>
            ) : (
              actionCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.id}
                    className={`flex items-center gap-3 p-4 rounded-lg border ${
                      card.urgent
                        ? 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900'
                        : 'bg-muted/50 border-border'
                    }`}
                  >
                    <Icon className={`h-5 w-5 shrink-0 ${card.urgent ? 'text-orange-600' : 'text-primary'}`} />
                    <p className={`text-sm flex-1 ${card.urgent ? 'text-orange-800 dark:text-orange-300 font-medium' : 'text-foreground'}`}>
                      {card.message}
                    </p>
                    <Link href={card.href}>
                      <Button size="sm" variant={card.urgent ? "default" : "outline"}>
                        {card.cta}
                      </Button>
                    </Link>
                  </div>
                );
              })
            )}
          </div>
        )}

        {!showOnboarding && (<div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {statsLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <Star className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="text-xs text-muted-foreground">Rating</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-rating">
                    {stats?.rating?.toFixed(1) || '—'}
                  </div>
                  <div className="flex items-center gap-0.5 text-xs text-muted-foreground mt-0.5">
                    {(stats?.ratingChange || 0) > 0 ? (
                      <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (stats?.ratingChange || 0) < 0 ? (
                      <TrendingDown className="h-3 w-3 text-red-500" />
                    ) : (
                      <Minus className="h-3 w-3" />
                    )}
                    <span>{Math.abs(stats?.ratingChange || 0).toFixed(1)}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {statsLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Reviews</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-reviews-count">
                    {stats?.totalReviews || 0}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    +{stats?.newReviewsThisWeek || 0} this week
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {statsLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <Wallet className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs text-muted-foreground">Kudoz</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-kudoz-balance">
                    {(stats?.kudozBalance || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    +{(stats?.kudozEarnedThisMonth || 0).toLocaleString()} mo
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>)}

        {!showOnboarding && recentPostsForDisplay.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
              <div>
                <CardTitle>Recent Posts</CardTitle>
                <CardDescription>Your latest content performance</CardDescription>
              </div>
              <Link href="/partner/post">
                <Button variant="ghost" size="sm">See all</Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentPostsForDisplay.map((post) => (
                  <div key={post.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    {post.images?.[0] ? (
                      <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-muted">
                        <img src={post.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ) : (
                      <div className="shrink-0 w-12 h-12 rounded-md bg-muted flex items-center justify-center">
                        <Camera className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm line-clamp-1">{post.text}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{post.likes}</span>
                        <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{post.comments}</span>
                        {post.views !== undefined && (
                          <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{post.views}</span>
                        )}
                        {post.orders !== undefined && post.orders > 0 ? (
                          <span className="flex items-center gap-1"><ShoppingBag className="h-3 w-3" />{post.orders} orders</span>
                        ) : null}
                        {post.revenue !== undefined && post.revenue > 0 ? (
                          <span className="flex items-center gap-1 text-green-600"><DollarSign className="h-3 w-3" />${(post.revenue / 100).toFixed(0)}</span>
                        ) : null}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      {!(post.orders && post.orders > 0) && (
                        <p className="text-[10px] text-muted-foreground/70 mt-1 italic">Order attribution coming soon</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {!showOnboarding && (<Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div>
              <CardTitle>Recent Orders</CardTitle>
              <CardDescription>Orders driven by your content</CardDescription>
            </div>
            {ordersData && (
              <div className="text-right">
                <p className="text-2xl font-bold">${(ordersData.total_earned / 100).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{ordersData.total_results} orders total</p>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : ordersData && ordersData.results.length > 0 ? (
              <div className="space-y-3">
                {ordersData.results.slice(0, 6).map((order) => {
                  const status = getOrderStatus(order);
                  const StatusIcon = status.icon;
                  return (
                    <div key={order.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="shrink-0">
                        <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{order.customer.name}</p>
                          <Badge variant="outline" className="capitalize shrink-0">{order.type}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Order #{order.id.slice(0, 8)} · {formatTime(order.progress.placed)}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-medium">${(order.total / 100).toFixed(2)}</p>
                        <div className={`flex items-center gap-1 text-xs ${status.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          <span>{status.label}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : ordersError ? (
              <div className="text-center py-8">
                <XCircle className="h-8 w-8 mx-auto text-destructive mb-2" />
                <p className="text-sm text-destructive font-medium">Failed to load orders</p>
                <p className="text-xs text-muted-foreground mt-1">Please check your connection and try again.</p>
              </div>
            ) : !eatzyToken ? (
              <div className="text-center py-8">
                <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Please log in with your Eatzy credentials to view orders.</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No orders yet. Post content to drive your first orders through Eatzy!
              </p>
            )}
          </CardContent>
        </Card>)}

        {!showOnboarding && (<Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <div>
              <CardTitle>Recent Reviews</CardTitle>
              <CardDescription>Latest customer feedback</CardDescription>
            </div>
            <Link href="/partner/reviews">
              <Button variant="ghost" size="sm">See all</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {reviewsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : recentReviews && recentReviews.length > 0 ? (
              <div className="space-y-4">
                {recentReviews.slice(0, 3).map((review) => (
                  <div key={review.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-1 shrink-0">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      <span className="font-medium">{review.rating}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm line-clamp-2">{review.text || 'No comment'}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {review.userName} · {new Date(review.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    {!review.hasReply && (
                      <Badge variant="outline" className="shrink-0 text-orange-600 border-orange-300">Needs reply</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No reviews yet. Post content regularly to get more customer feedback!
              </p>
            )}
          </CardContent>
        </Card>)}
      </div>
    </PartnerLayout>
  );
}

function OnboardingStep({ number, title, description, done, href }: {
  number: number;
  title: string;
  description: string;
  done: boolean;
  href?: string;
}) {
  const content = (
    <div className="flex items-start gap-3">
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
        done 
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
          : 'bg-primary/10 text-primary'
      }`}>
        {done ? <CheckCircle className="h-4 w-4" /> : number}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : ''}`}>{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {href && !done && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
    </div>
  );

  if (href && !done) {
    return (
      <Link href={href}>
        <div className="hover-elevate rounded-lg p-2 -m-2 cursor-pointer">
          {content}
        </div>
      </Link>
    );
  }

  return <div className="p-2 -m-2">{content}</div>;
}
